"use client";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import type { AiHotel } from "@/feature/ai-planner/types/ai-chat.types";
import {
  buildHotelImageUrl,
  dedupeHotelImages,
  HOTEL_FALLBACK_IMAGE,
} from "@/feature/hotel/search/lib/hotel-image";
import Autoplay from "embla-carousel-autoplay";
import Fade from "embla-carousel-fade";
import Image from "next/image";
import type { SyntheticEvent } from "react";
import { memo, useMemo, useRef } from "react";

function handleImageError(event: SyntheticEvent<HTMLImageElement>) {
  const target = event.currentTarget;

  if (target.src.endsWith(HOTEL_FALLBACK_IMAGE)) {
    return;
  }

  target.src = HOTEL_FALLBACK_IMAGE;
}

function asTrimmedString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function getImageUrls(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }

    return [
      asTrimmedString((item as { image_url?: unknown }).image_url),
    ].filter(Boolean);
  });
}

function getHotelImageUrls(hotel: AiHotel) {
  const nestedImages =
    hotel.images && typeof hotel.images === "object" ? hotel.images : undefined;

  return dedupeHotelImages([
    asTrimmedString(nestedImages?.main_image),
    ...getImageUrls(nestedImages?.additional_images),
    asTrimmedString(hotel.main_image),
    ...getImageUrls(hotel.additional_images),
    asTrimmedString(hotel.image_url),
    asTrimmedString(hotel.thumbnail),
  ]);
}

export const ChatHotelImageCarousel = memo(function ChatHotelImageCarousel({
  hotel,
}: {
  hotel: AiHotel;
}) {
  const autoplayPluginRef = useRef(
    Autoplay({
      delay: 3500,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  );

  const imageItems = useMemo(() => {
    const rawUrls = getHotelImageUrls(hotel);

    if (!rawUrls.length) {
      return [
        {
          key: "hotel-fallback",
          src: HOTEL_FALLBACK_IMAGE,
          alt: hotel.name ?? "Hotel option",
        },
      ];
    }

    return rawUrls.map((imageUrl, index) => ({
      key: `${hotel.hotel_code ?? hotel.name ?? "hotel"}-${index}`,
      src: buildHotelImageUrl(imageUrl),
      alt: hotel.name ?? "Hotel option",
    }));
  }, [hotel]);

  return (
    <Carousel
      className="h-full w-full"
      opts={{ loop: imageItems.length > 1 }}
      plugins={imageItems.length > 1 ? [autoplayPluginRef.current, Fade()] : []}
    >
      <CarouselContent className="ml-0 h-full">
        {imageItems.map((image) => (
          <CarouselItem key={image.key} className="pl-0">
            <div className="relative h-48 w-full overflow-hidden bg-muted sm:h-[160px]">
              <Image
                src={image.src}
                alt={image.alt}
                fill
                sizes="(max-width: 640px) 100vw, 140px"
                className="object-cover"
                unoptimized
                onError={handleImageError}
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
});
